import { computed, onMounted, ref, type Ref } from 'vue';
import { invitationsClient, ssoClient } from '~/services';
import { emailDomain, isVerifiedEmailDomain } from '~/utils/emailDomain';

const outsideVerifiedDomainsMessage = (domains: string[]): string =>
    `This team requires SSO, so invitations can only go to a verified email domain (${domains.join(', ')}).`;

const outsideAllowedDomainsMessage = (domains: string[]): string =>
    `Invitations to this team can only go to an allowed email domain (${domains.join(', ')}).`;

export function useInviteEmailDomains(teamId: number, email: Ref<string>) {
    const requireSso = ref(false);
    const verifiedDomains = ref<string[]>([]);
    const allowedDomains = ref<string[]>([]);
    const loaded = ref(false);

    const loadVerifiedDomains = async () => {
        const { data } = await ssoClient.getVerifiedDomains(teamId);
        requireSso.value = data.require_sso;
        verifiedDomains.value = data.domains;
    };

    const loadAllowedDomains = async () => {
        const { data } = await invitationsClient.getEmailDomainInvites(teamId);
        allowedDomains.value = data.domains;
    };

    onMounted(async () => {
        // Fail open; the server enforces both lists.
        await Promise.allSettled([loadVerifiedDomains(), loadAllowedDomains()]);
        loaded.value = true;
    });

    const typedDomain = computed(() => emailDomain(email.value));
    const willUseSso = computed(() => email.value !== '' && isVerifiedEmailDomain(email.value, verifiedDomains.value));
    const outsideAllowedDomains = computed(
        () =>
            loaded.value &&
            allowedDomains.value.length > 0 &&
            typedDomain.value !== null &&
            !allowedDomains.value.includes(typedDomain.value)
    );
    const outsideVerifiedDomains = computed(
        () => loaded.value && requireSso.value && typedDomain.value !== null && !willUseSso.value
    );
    const blocked = computed(() => outsideAllowedDomains.value || outsideVerifiedDomains.value);
    const blockedMessage = computed(() => {
        if (outsideAllowedDomains.value) {
            return outsideAllowedDomainsMessage(allowedDomains.value);
        }

        return outsideVerifiedDomains.value ? outsideVerifiedDomainsMessage(verifiedDomains.value) : '';
    });

    return { blocked, blockedMessage, outsideAllowedDomains, willUseSso };
}
